/**
 * 商品卡片組件
 * 顯示商品資訊並提供加入購物車功能
 */

import { useCart } from '../hooks/useCart';
import { formatTwdFromCents } from '@pos/shared/money';

/** 規格選項（API 回傳 snake_case，前端統一用 camelCase） */
export interface ProductVariant {
  id?: string;
  combination?: Record<string, string>;
  unitPriceTwd: number;
}

/**
 * 商品資料介面
 */
export interface Product {
  id: number | string; // 支援 number (舊) 和 string UUID (新)
  name: string;
  /** 售價 (分)，用於計算與顯示 */
  price: number;
  /** 原價 (分)；若有且大於售價則顯示劃線原價與紅色售價 */
  listPrice?: number;
  thumbnail?: string;
  description?: string;
  category?: string;
  type?: 'physical' | 'service' | 'voucher' | 'subscription';
  /** 折扣資訊（簡短標籤，如 "打九折"） */
  discountLabel?: string;
  /** 點數資訊（簡短標籤，如 "2倍點數"） */
  pointsLabel?: string;
  /** 是否為多規格品；為 true 時點擊應開啟規格選擇 Modal */
  hasVariants?: boolean;
  /** 規格列表（hasVariants 時由 API 回傳） */
  variants?: ProductVariant[];
}

/**
 * 商品卡片 Props
 * onProductClick：點擊卡片時呼叫；若提供則由父層決定加入購物車或開啟規格 Modal
 * onAddToCart：僅在未提供 onProductClick 且直接加入購物車時呼叫（無規格品）
 */
export interface ProductCardProps {
  product: Product;
  onProductClick?: (product: Product) => void;
  onAddToCart?: (product: Product) => void;
}

/**
 * 商品卡片組件
 */
export function ProductCard({
  product,
  onProductClick,
  onAddToCart,
}: ProductCardProps) {
  const { addItem } = useCart();

  /**
   * 處理加入購物車（無規格品直接加入時使用）
   */
  const handleAddToCart = () => {
    const productId = String(product.id);
    const id = productId;
    addItem({
      id,
      productId,
      name: product.name,
      price: product.price,
      thumbnail: product.thumbnail,
    });
    onAddToCart?.(product);
  };

  /**
   * 點擊卡片：若有 onProductClick 則交給父層（可開規格 Modal）；否則無規格則直接加入購物車
   */
  const handleClick = () => {
    if (onProductClick) {
      onProductClick(product);
      return;
    }
    handleAddToCart();
  };

  // 根據商品類型設定邊框顏色
  const getTypeBorderColor = () => {
    switch (product.type) {
      case 'physical':
        return 'border-green-500';
      case 'service':
        return 'border-purple-500';
      case 'voucher':
        return 'border-orange-500';
      case 'subscription':
        return 'border-pink-500';
      default:
        return 'border-gray-300';
    }
  };

  return (
    <div
      onClick={handleClick}
      className="rounded-2xl overflow-hidden bg-white shadow-sm transition-all duration-200 ease-out cursor-pointer hover:opacity-90 hover:shadow-md active:opacity-70"
    >
      {/* 商品圖片 - 圓角方形，帶類型顏色邊框；下方無留白讓文字貼近 */}
      <div className="aspect-square bg-white flex items-center justify-center pt-6 px-6 pb-0 relative overflow-visible">
        {product.thumbnail ? (
          <div
            className={`aspect-square w-full rounded-[2rem] overflow-hidden bg-white border-4 ${getTypeBorderColor()} relative shrink-0`}
          >
            <img
              src={product.thumbnail}
              alt={product.name}
              className="w-full h-full object-cover"
            />
          </div>
        ) : (
          <div
            className={`aspect-square w-full rounded-[2rem] bg-gray-100 flex items-center justify-center border-4 ${getTypeBorderColor()} relative shrink-0`}
          >
            <svg
              className="w-16 h-16 text-gray-400"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
                clipRule="evenodd"
              />
            </svg>
          </div>
        )}
        {/* 折扣和點數標籤 - 與圖齊高（top-6 與圖片區塊起始一致） */}
        {(product.discountLabel || product.pointsLabel) && (
          <div className="absolute top-6 right-2 flex flex-col gap-1 z-10">
            {product.discountLabel && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-xs font-medium bg-red-500 text-white shadow-md whitespace-nowrap">
                {product.discountLabel}
              </span>
            )}
            {product.pointsLabel && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-xs font-medium bg-blue-500 text-white shadow-md whitespace-nowrap">
                {product.pointsLabel}
              </span>
            )}
          </div>
        )}
      </div>

      {/* 商品資訊（圖片與文字間距略縮，品名與圖更近） */}
      <div className="px-4 pt-1.5 pb-2 text-center">
        <h3 className="text-sm text-gray-600 mb-0.5 line-clamp-1 leading-tight">
          {product.name}
        </h3>

        {/* 價格：售價 < 原價時顯示劃線原價與紅色特價，否則只顯示售價 */}
        <div className="flex items-center justify-center gap-2 flex-wrap">
          {typeof product.listPrice === 'number' &&
          product.listPrice > product.price ? (
            <>
              <span className="text-sm text-gray-500 line-through">
                {formatTwdFromCents(product.listPrice)}
              </span>
              <span className="text-lg font-bold text-red-600">
                {formatTwdFromCents(product.price)}
              </span>
            </>
          ) : (
            <span className="text-lg font-bold text-blue-600">
              {formatTwdFromCents(product.price)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
